import React, { createContext, useContext, useReducer, useEffect } from 'react';
import Cookies from 'js-cookie';
import logger from '../utils/logger';

const AuthContext = createContext();

// Action types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_USER: 'SET_USER',
  CLEAR_ERROR: 'CLEAR_ERROR'
};

// Initial state
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null
      };
    
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null
      };
    
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload
      };
    
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      };
    
    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null
      };
    
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    
    default:
      return state;
  }
};

// Provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check for existing token on mount and also listen for storage changes
  useEffect(() => {
    const checkTokenAndUpdate = () => {
      logger.log('[AUTH]  checkTokenAndUpdate called, current state:', {
        isAuthenticated: state.isAuthenticated,
        isLoading: state.isLoading,
        hasUser: !!state.user,
        hasToken: !!state.token
      });

      const token = localStorage.getItem('cti_admin_token') || Cookies.get('cti_admin_token');
      const userData = localStorage.getItem('cti_admin_user') || Cookies.get('cti_admin_user');

      logger.log('[AUTH]  Storage check:', {
        hasStorageToken: !!token,
        hasStorageUserData: !!userData
      });

      if (token && userData) {
        try {
          // Check if token is expired before using it
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.exp && payload.exp * 1000 < Date.now()) {
            logger.log('[AUTH] Token expired, clearing storage');
            localStorage.removeItem('cti_admin_token');
            localStorage.removeItem('cti_admin_user');
            dispatch({ type: AUTH_ACTIONS.LOGOUT });
            return;
          }

          const user = JSON.parse(userData);
          logger.log('[AUTH]  Valid token and user data found, dispatching SET_USER');
          dispatch({
            type: AUTH_ACTIONS.SET_USER,
            payload: { user, token }
          });
        } catch (error) {
          console.error('[AUTH] ❌ Error parsing user data from storage:', error);
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
        }
      } else {
        // No token o user data - asegurar que isLoading sea false
        logger.log('[AUTH]  No token/userdata found - dispatching LOGOUT to ensure isLoading=false');
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      }
    };

    // Verificación inicial
    checkTokenAndUpdate();

    // 🔒 NUEVO: Escuchar cambios en localStorage para detectar logout inmediatamente
    const handleStorageChange = (e) => {
      if (e.key === 'cti_admin_token' && !e.newValue) {
        logger.log('[AUTH]  Token eliminado via storage event - forzando logout');
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []); // 🔧 Solo ejecutar una vez al mount para evitar loops

  // Login function
  const login = async (username, password, options = {}) => {
    const { onPreSuccess, delayMs = 1500 } = options;
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      const response = await fetch('/api/admin/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (data.success) {
        const { token, admin } = data.data;

        // Store in both localStorage and cookies for compatibility
        localStorage.setItem('cti_admin_token', token);
        localStorage.setItem('cti_admin_user', JSON.stringify(admin));
        Cookies.set('cti_admin_token', token, { expires: 1 }); // 1 day
        Cookies.set('cti_admin_user', JSON.stringify(admin), { expires: 1 });

        // Callback previo para animaciones en login
        if (typeof onPreSuccess === 'function') {
          try {
            onPreSuccess();
          } catch (err) {
            console.error('onPreSuccess callback error:', err);
          }
        }

        // Pequeño delay para permitir mostrar animaciones en login antes de redirigir
        await new Promise((resolve) => setTimeout(resolve, delayMs));

        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user: admin, token }
        });

        return { success: true };
      } else {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAILURE,
          payload: data.message || 'Error de autenticación'
        });
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Login error:', error);
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: 'Error de conexión con el servidor'
      });
      return { success: false, message: 'Error de conexión con el servidor' };
    }
  };

  // Logout function
  const logout = async () => {
    logger.log('[AUTH] ===== INICIANDO LOGOUT EN AUTHCONTEXT =====');
    logger.log('[AUTH] Estado actual:', {
      hasUser: !!state.user,
      hasToken: !!state.token,
      isAuthenticated: state.isAuthenticated,
      isLoading: state.isLoading
    });

    let serverLogoutSuccess = false;

    try {
      // Call logout endpoint if token exists
      const token = localStorage.getItem('cti_admin_token') || Cookies.get('cti_admin_token');
      logger.log('[AUTH] Token encontrado:', !!token);

      if (token) {
        logger.log('[AUTH] Enviando solicitud POST a /api/admin/auth/logout...');

        const response = await fetch('/api/admin/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        logger.log('[AUTH] Respuesta del servidor:', {
          ok: response.ok,
          status: response.status,
          statusText: response.statusText
        });

        if (response.ok) {
          const responseData = await response.json();
          logger.log('[AUTH]  Logout del servidor exitoso:', responseData);
          serverLogoutSuccess = true;
        } else {
          console.error('[AUTH] ❌ Error del servidor al hacer logout:', {
            status: response.status,
            statusText: response.statusText
          });
        }
      } else {
        logger.log('[AUTH]  No se encontró token para cerrar sesión en servidor');
        serverLogoutSuccess = true; // No token means already logged out
      }
    } catch (error) {
      console.error('[AUTH] ❌ Error de conexión al hacer logout en servidor:', error);
    } finally {
      // Siempre limpiar el estado local, independientemente del resultado del servidor
      logger.log('[AUTH]  Limpiando datos locales...');

      logger.log('[AUTH] Antes de limpiar localStorage:', {
        token: !!localStorage.getItem('cti_admin_token'),
        user: !!localStorage.getItem('cti_admin_user')
      });

      // Clear both localStorage and cookies
      localStorage.removeItem('cti_admin_token');
      localStorage.removeItem('cti_admin_user');
      Cookies.remove('cti_admin_token');
      Cookies.remove('cti_admin_user');

      logger.log('[AUTH] Después de limpiar localStorage:', {
        token: !!localStorage.getItem('cti_admin_token'),
        user: !!localStorage.getItem('cti_admin_user')
      });

      // Update context state
      logger.log('[AUTH] Disparando acción LOGOUT...');
      dispatch({ type: AUTH_ACTIONS.LOGOUT });

      logger.log('[AUTH] Estado después del dispatch:', {
        hasUser: !!state.user,
        hasToken: !!state.token,
        isAuthenticated: state.isAuthenticated
      });

      logger.log('[AUTH]  Logout local completado');

      if (!serverLogoutSuccess) {
        logger.warn('[AUTH] ⚠️ ADVERTENCIA: La sesión se limpió localmente pero puede que no se haya cerrado correctamente en el servidor');
        // NO lanzar error - permitir que el logout local proceda
        // throw new Error('Error al cerrar sesión en el servidor');
      }
    }

    logger.log('[AUTH] ===== LOGOUT EN AUTHCONTEXT COMPLETADO =====');
  };

  // Update profile function
  const updateProfile = async (profileData) => {
    try {
      const response = await fetch('/api/admin/auth/profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${state.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData),
      });

      const data = await response.json();

      if (data.success) {
        const updatedUser = { ...state.user, ...data.data.admin };
        localStorage.setItem('cti_admin_user', JSON.stringify(updatedUser));
        Cookies.set('cti_admin_user', JSON.stringify(updatedUser), { expires: 1 });

        dispatch({
          type: AUTH_ACTIONS.SET_USER,
          payload: { user: updatedUser, token: state.token }
        });

        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Update profile error:', error);
      return { success: false, message: 'Error de conexión con el servidor' };
    }
  };

  // Change password function
  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await fetch('/api/admin/auth/password', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${state.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const data = await response.json();

      return {
        success: data.success,
        message: data.message
      };
    } catch (error) {
      console.error('Change password error:', error);
      return { success: false, message: 'Error de conexión con el servidor' };
    }
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Verify token function
  const verifyToken = async () => {
    if (!state.token) return false;

    try {
      const response = await fetch('/api/admin/auth/verify', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${state.token}`,
        },
      });

      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('Token verification error:', error);
      return false;
    }
  };

  const updateStoredUser = (partialUser) => {
    const mergedUser = {
      ...(state.user || {}),
      ...(partialUser || {})
    };

    try {
      localStorage.setItem('cti_admin_user', JSON.stringify(mergedUser));
      Cookies.set('cti_admin_user', JSON.stringify(mergedUser));
    } catch (err) {
      console.error('[AUTH] No se pudo persistir el usuario actualizado:', err);
    }

    dispatch({
      type: AUTH_ACTIONS.SET_USER,
      payload: { user: mergedUser, token: state.token }
    });
  };

  const value = {
    ...state,
    login,
    logout,
    updateProfile,
    changePassword,
    updateStoredUser,
    clearError,
    verifyToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
